// Per-species proportions for the high-fidelity unit path (docs/02-systems/unit-fidelity.md, stage v3).
// Prefab data: the builder (apps/app/src/render/voxelGeometryCache.ts) reads `prefab.hifi`; lo-fi ignores it.
// Values stay within +-12% so a unit keeps its tile footprint (bounding size within ~15% of lo-fi).

import type { CharacterPrefab, HiFiTraits } from './types.js';

/** Broad champions and heavy infantry. */
const BROAD: HiFiTraits = { build: 1.05, limb: 1.1 };
/** Lither archers, scouts and casters. */
const LITHER: HiFiTraits = { build: 0.94, limb: 0.86 };
/** Hulking brutes: broad but not taller. */
const BRUTE: HiFiTraits = { build: 1.05, limb: 1.1 };

/** Rank-and-file foot soldiers: a little broader than the lo-fi figure, no taller. */
const SOLDIERLY: HiFiTraits = { build: 1.03, limb: 1.05 };
/** Robed priests and healers: slight and soft. */
const ROBED: HiFiTraits = { build: 0.97, limb: 0.92 };
/** The walking dead: gaunt, long-limbed. */
const GAUNT: HiFiTraits = { build: 0.92, limb: 0.9 };

export const HIFI_TRAITS: Readonly<Record<string, HiFiTraits>> = {
  knight: BROAD,
  paladin: BROAD,
  templar: BROAD,
  dark_templar: BROAD,
  death_knight: BROAD,
  warlord: BROAD,
  ogre: BRUTE,
  orc: BRUTE,
  treant: BRUTE,
  bowman: LITHER,
  skeleton_archer: LITHER,
  spy: LITHER,
  explorer: LITHER,
  mage: LITHER,
  wizard: LITHER,
  banshee: LITHER,
  // `soldier` (the rank-and-file baseline), `zealot` and `demon` stay unlisted: their
  // limbs already stretch past the 15% footprint rule when scaled.
  guard: SOLDIERLY,
  bronze_smith: BROAD,
  chaos_lord: BROAD,
  cleric: ROBED,
  sorcerer: ROBED,
  blood_priest: ROBED,
  archmage: ROBED,
  lich: GAUNT,
  wraith: GAUNT,
  skeleton: GAUNT,
  zombie: GAUNT,
  bandit: LITHER,
};

/** The prefab with its hi-fi traits attached (unchanged when the id has none). */
export function withHiFiTraits(id: string, prefab: CharacterPrefab): CharacterPrefab {
  const traits = HIFI_TRAITS[id];
  return traits ? { ...prefab, hifi: traits } : prefab;
}
